import { UI_CONFIG, API_CONFIG, AIProvider } from '../config';

/**
 * Hậu tố hiển thị chi phí trên nút/nhãn khi `ui_show_credit_costs` bật (đồng bộ backend).
 * Dùng chung thay vì nhân bản `getCostLabel` trong từng component.
 *
 * Khi truyền `provider` (+ `model` tuỳ chọn), hàm sẽ áp `multiplier` từ
 * `API_CONFIG.MODEL_PRICING` để hiển thị chi phí ước tính chính xác cho cặp
 * provider × model. Đây là hệ số nhân vào baseCost — phần phụ trội theo
 * token sẽ được tính ở backend khi AI trả về usage thực tế (chỉ ước tính ở UI).
 */
export function getCreditCostSuffix(
  cost: number,
  provider?: AIProvider | string | null,
  model?: string | null,
): string {
  if (!UI_CONFIG.SHOW_CREDIT_COSTS || cost <= 0) return '';
  const label = UI_CONFIG.CREDIT_LABEL || 'Tín dụng';

  const multiplier = resolveMultiplier(provider, model);
  if (multiplier === 1 || !provider) {
    return ` (-${cost} ${label})`;
  }

  const adjusted = Math.max(1, Math.ceil(cost * multiplier));
  if (adjusted === cost) {
    return ` (-${cost} ${label})`;
  }
  const sign = multiplier > 1 ? '×' : '×';
  return ` (-${adjusted} ${label} ${sign}${formatMultiplier(multiplier)})`;
}

/**
 * Lấy multiplier cho cặp (provider, model). Nếu không có model → dùng model mặc định
 * tương ứng theo provider. Fallback `_default` → 1.0.
 */
export function resolveMultiplier(
  provider?: AIProvider | string | null,
  model?: string | null,
): number {
  if (!provider) return 1;
  const pricing = (API_CONFIG.MODEL_PRICING || {}) as Record<string, Record<string, { multiplier: number }>>;
  const providerKey = String(provider);
  const providerPricing = pricing[providerKey] || {};

  const resolvedModel = model
    || (providerKey === 'gemini' ? API_CONFIG.GEMINI_TEXT_MODEL
        : providerKey === 'openai' ? API_CONFIG.OPENAI_TEXT_MODEL
        : providerKey === 'anthropic' ? API_CONFIG.ANTHROPIC_TEXT_MODEL
        : '');

  const entry = (resolvedModel && providerPricing[resolvedModel])
    || providerPricing._default
    || (pricing as any)._default
    || { multiplier: 1 };
  const m = Number((entry as any).multiplier);
  return Number.isFinite(m) && m > 0 ? m : 1;
}

function formatMultiplier(m: number): string {
  if (m % 1 === 0) return m.toFixed(0);
  return m.toFixed(1).replace(/\.0$/, '');
}
